/**
 * React Query State Management for Club Data
 * 
 * Provides pre-computed UI states so components can easily
 * determine what to render without complex conditional logic.
 * 
 * This hook dramatically simplifies component code by centralizing
 * all loading/error/success state logic.
 */

import type { ClubQueryError } from './useClubQueryError';
import type { ClubWithTenant } from '../types';

/**
 * Pre-computed UI state for club query
 */
export interface ClubQueryState {
  /** Loading state */
  status: 'idle' | 'loading' | 'error' | 'success';
  
  /** Whether to show loading UI */
  shouldShowLoader: boolean;
  
  /** Whether to show error UI */
  shouldShowError: boolean;
  
  /** Whether to show content UI */
  shouldShowContent: boolean;
  
  /** Club data (only available when shouldShowContent is true) */
  club: ClubWithTenant | null;
  
  /** Processed error (only available when shouldShowError is true) */
  error: ClubQueryError | null;
  
  /** Whether query is currently fetching */
  isFetching: boolean;
  
  /** Whether this is the initial load */
  isInitialLoading: boolean;
}

/**
 * Hook to get pre-computed UI state for club query
 * 
 * Simplifies component rendering by providing clear boolean flags
 * for what to display.
 * 
 * @param data - Club data from useClubQuery
 * @param isLoading - Loading state from useClubQuery
 * @param isFetching - Fetching state from useClubQuery
 * @param clubQueryError - Processed error from useClubQuery
 * @returns Pre-computed UI state
 * 
 * @example
 * ```tsx
 * function ClubInfo() {
 *   const query = useClubQuery();
 *   const state = useClubQueryState(
 *     query.data,
 *     query.isLoading,
 *     query.isFetching,
 *     query.clubQueryError
 *   );
 *   
 *   // Simple, declarative rendering
 *   if (state.shouldShowLoader) {
 *     return <LoadingSpinner />;
 *   }
 *   
 *   if (state.shouldShowError && state.error) {
 *     return (
 *       <BookingError
 *         title={state.error.title}
 *         message={state.error.detailedMessage}
 *         action={state.error.onRetry ? {
 *           label: 'Retry',
 *           onClick: state.error.onRetry
 *         } : undefined}
 *       />
 *     );
 *   }
 *   
 *   if (state.shouldShowContent && state.club) {
 *     return <ClubDetails club={state.club} />;
 *   }
 *   
 *   return null;
 * }
 * ```
 */
export function useClubQueryState(
  data: ClubWithTenant | undefined,
  isLoading: boolean,
  isFetching: boolean,
  clubQueryError: ClubQueryError | null
): ClubQueryState {
  // Determine status
  let status: ClubQueryState['status'] = 'idle';
  if (clubQueryError) {
    status = 'error';
  } else if (isLoading) {
    status = 'loading';
  } else if (data) {
    status = 'success';
  }
  
  // Compute UI flags
  const shouldShowLoader = isLoading && !clubQueryError;
  const shouldShowError = !!clubQueryError;
  const shouldShowContent = !!data && !clubQueryError;
  const isInitialLoading = isLoading && !data;
  
  return {
    status,
    shouldShowLoader,
    shouldShowError,
    shouldShowContent,
    club: data || null,
    error: clubQueryError,
    isFetching,
    isInitialLoading,
  };
}

/**
 * Helper to get error category for monitoring
 * 
 * @param state - Club query state
 * @returns Error category or null
 */
export function getStateErrorCategory(
  state: ClubQueryState
): 'api_error' | 'data_validation' | null {
  if (!state.error) return null;
  return state.error.isConnectivity ? 'api_error' : 'data_validation';
}

/**
 * Helper to get error type for monitoring
 * 
 * @param state - Club query state
 * @returns Error type or null
 */
export function getStateErrorType(state: ClubQueryState): string | null {
  if (!state.error) return null;
  if (state.error.isNetwork) return 'network_error';
  if (state.error.isServer) return 'server_error';
  if (state.error.isValidation) return 'validation_error';
  return 'fetch_error';
}
